import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ApiHelper } from "../../utils/ApiHelper";
import { COMPANY_DETAIL_OF_USER, UPLOAD_COMPANY_LOGO, UPDATE_COMPANY_DETAIL } from "../../lib/API_KEYS";

const UpdateSellerAdditionalInfoForm = () => {
  const navigate = useNavigate();
  const [uploadedLogo, setUploadedLogo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [initialData, setInitialData] = useState(null);

  const INDUSTRIES = [
    "Automotive",
    "Aerospace",
    "Consumer Goods",
    "Electronics & Communications",
    "Medical Equipment",
    "Industrial Machinery",
    "Others"
  ];

  const showToast = (msg, type = 'success') => {
    toast.dismiss();
    toast[type](msg, { toastId: 'globalToast', position: 'top-right', autoClose: 3000, hideProgressBar: true, theme: 'colored' });
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('File must be <= 2MB.', 'error');
      return;
    }
    setUploadedLogo(file);
  };

  const updateArrayItem = (arrayKey, index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev[arrayKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayKey]: arr };
    });
  };

  const addArrayItem = (arrayKey, template) => {
    setFormData((prev) => ({ ...prev, [arrayKey]: [...(prev[arrayKey] || []), template] }));
  };

  const removeArrayItem = (arrayKey, index) => {
    setFormData((prev) => ({ ...prev, [arrayKey]: (prev[arrayKey] || []).filter((_, i) => i !== index) }));
  };

  const renderArraySection = (arrayKey, fields, placeholderLabels, addLabel, template) => {
    const rows = formData?.[arrayKey] || [];
    return (
      <div className="mb-3">
        {rows.length > 0 && (
          <div className="d-none d-md-flex fw-bold gap-2 mb-1">
            {fields.map((field, idx) => <div key={idx} style={{ flex: field.flex || 1 }}>{placeholderLabels[idx]}</div>)}
          </div>
        )}
        {rows.map((row, idx) => (
          <div className="d-flex flex-column flex-md-row align-items-start gap-2 mb-2" key={idx}>
            {fields.map((field, fIdx) => (
              <div key={fIdx} className="w-100">
                <div className="d-md-none fw-bold">{placeholderLabels[fIdx]}</div>
                <input
                  className="form-control"
                  style={{ flex: field.flex || 1 }}
                  placeholder={placeholderLabels[fIdx]}
                  value={row[field.key] || ''}
                  onChange={(e) => updateArrayItem(arrayKey, idx, field.key, e.target.value)}
                />
              </div>
            ))}
            <button type="button" className="btn btn-outline-danger mt-2 mt-md-0" onClick={() => removeArrayItem(arrayKey, idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-outline-dark mt-1" onClick={() => addArrayItem(arrayKey, template)}>+ {addLabel}</button>
      </div>
    );
  };

  const isFormChanged = () => JSON.stringify(formData) !== JSON.stringify(initialData) || uploadedLogo !== null;

  const fetchFormData = async () => {
    try {
      const res = await new ApiHelper().FetchFromServer(
        ...COMPANY_DETAIL_OF_USER
      );
      const data = res.data;

      // const defaults = {
      //   manpower: { skilledWorkers: 0, totalEmployees: 0, engDegreeDiploma: 0, designDept: 0, qualityControl: 0 },
      //   oemDetails: [],
      //   majorCustomers: [],
      //   exportDetails: [],
      //   factoryLocations: [],
      //   machiningFacilities: [],
      //   inspectionFacilities: [],
      //   annualTurnover3yr: [],
      // };

      const filledData = { ...data }; //...defaults,
      setFormData(filledData);
      setInitialData(filledData);
    } catch (err) {
      console.error('Failed to fetch form data:', err);
      showToast('Failed to load data', 'error');
    }
  };

  const handleSelect = (e) => {
    const value = e.target.value;

    if (!value) return;

    // If selecting Others → only add "Others"
    if (value === "Others") {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry, value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry.filter(i => i !== "Others"), value],
        otherIndustry: ""
      }));
    }
  };

  const removeChip = (item) => {
    setFormData(prev => {
      const updated = prev.industry.filter(i => i !== item);

      return {
        ...prev,
        industry: updated,
        otherIndustry: item === "Others" ? "" : prev.otherIndustry
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const { confirmCheck, website, email, linkedIn, aboutUs, address, name, industry, ceoName, ceoMobile, ceoEmail, phone, countryCode, ceoCountryCode, ...rest } = formData;

    if (!confirmCheck) {
      showToast('Please confirm before submitting.', 'error');
      return;
    }

    // Check for uploaded logo (required for onboarding)
    if (!uploadedLogo && !formData.logo) {
      showToast('Please upload a company logo.', 'error');
      return;
    }

    // Check industry array is not empty
    if (!industry || industry.length === 0) {
      showToast('Please select at least one industry.', 'error');
      return;
    }

    // Check all other required fields
    if (!website || !email || !linkedIn || !address || !aboutUs || !name || !ceoName || !ceoMobile || !ceoEmail || !phone || !countryCode || !ceoCountryCode) {
      toast.error('Please fill all the required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      let logo = formData.logo || '';
      if (uploadedLogo) {
        const uploadRes = await new ApiHelper().FetchFromServer(
          ...UPLOAD_COMPANY_LOGO,
          undefined,
          { fileName: uploadedLogo.name, fileType: uploadedLogo.type, fileSize: uploadedLogo.size },
        );

        if (uploadRes.data?.isError || !uploadRes.data?.signedRequest) {
          console.error("Failed to get upload URL");
          return;
        }

        // Upload to S3
        await fetch(uploadRes.data.signedRequest, {
          method: "PUT", body: uploadedLogo, headers: {
            "Content-Type": uploadedLogo.type,
          },
        });
        logo = uploadRes.data.fileUrl;
      }

      // If resubmitting after Query Raised, change status back to In Review
      const finalData = { 
        ...formData, 
        logo, 
        status: 'In Review', 
        type: 'Seller'
      };

      await new ApiHelper().FetchFromServer(
        ...UPDATE_COMPANY_DETAIL,
        undefined,
        finalData,
      );

      showToast('Submitted successfully!', 'success');
      setInitialData(finalData);
      setUploadedLogo(null);
      setTimeout(() => navigate('/seller-dashboard'), 2000);
    } catch (err) {
      showToast('Submission failed.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => { fetchFormData(); }, []);

  if (!formData) return <div>Loading...</div>;

  return (
    <div className="container py-4">
      <h4 className="mb-4">Update Seller Additional Info</h4>
      
      {/* Show admin concern if status is Query Raised */}
      {formData.status === "Query Raised" && formData.concern && (
        <div className="alert alert-warning mb-4" role="alert">
          <h6 className="alert-heading">
            <strong>⚠️ Admin Feedback:</strong>
          </h6>
          <p className="mb-0">{formData.concern}</p>
          <hr />
          <small className="text-muted">
            Please address the concerns above and resubmit your application.
          </small>
        </div>
      )}
      
      <hr />

      <h6>Company Information</h6>
      <div className="row g-3 mb-4">

        <div className="row g-3">
          <div className="col-md-6">
            <div className="d-flex flex-wrap mb-1 gap-2">
              {formData?.industry?.map(item => (
                <span className="chip" key={item}>
                  {item}
                  <span className="chip-close" onClick={() => removeChip(item)}>✕</span>
                </span>
              ))}
            </div>
            <select className="form-select" onChange={handleSelect}>
              <option value="">Select Industry </option>
              {INDUSTRIES.filter(item => !formData.industry.includes(item))
                .map(item => (
                  <option key={item} value={item}>{item}</option>
                ))}
            </select>
          </div>

          {formData.industry.includes("Others") && (
            <div className="col-md-6">
              <label htmlFor="otherIndustry" className="form-label">Specify Industry</label>
              <input
                className="form-control mt-2"
                placeholder="Specify industry"
                value={formData.otherIndustry}
                onChange={(e) => setFormData(prev => ({ ...prev, otherIndustry: e.target.value }))}
              />
            </div>
          )}

        </div>
        <div className="col-md-6">
          <label className="form-label">Industry Expertise</label>
          <select id="industryExpertise" value={formData.industryExpertise || ''} onChange={handleChange} className="form-select">
            <option>Precision Machining</option><option>Injections Moulds</option>
            <option>Die Casting Components</option><option>Sheet Metal / Stamping</option>
            <option>Forging</option><option>Sub-assembly</option><option>Aerospace Components</option>
            <option>Engineering & Design Services</option><option>Prototyping</option><option>Others</option>
          </select>
          {formData.industryExpertise === 'Others' && (
            <input id="otherIndustryExpertise" value={formData.otherIndustryExpertise || ''} onChange={handleChange} className="form-control mt-2" placeholder="Specify Expertise" />
          )}
        </div>
        <div className="col-md-6"><label className="form-label">Company Name <span style={{ color: "red" }}>*</span></label>
          <input id="name" value={formData.name || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Address <span style={{ color: "red" }}>*</span></label>
          <input id="address" value={formData.address || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6">
          <label htmlFor="countryCode" className="form-label">Country Code <span style={{ color: "red" }}>*</span></label>
          <input id="countryCode" value={formData.countryCode} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your country code" />
        </div>
        <div className="col-md-6"><label className="form-label">Phone <span style={{ color: "red" }}>*</span></label>
          <input id="phone" value={formData.phone || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Fax</label>
          <input id="faxNumber" value={formData.faxNumber || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Email  <span style={{ color: "red" }}>*</span></label>
          <input id="email" value={formData.email || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Website  <span style={{ color: "red" }}>*</span></label>
          <input id="website" value={formData.website || ''} onChange={handleChange} className="form-control" />
        </div>
        <div className="col-md-6">
          <label htmlFor="linkedIn" className="form-label">LinkedIn <span style={{ color: "red" }}>*</span></label>
          <input id="linkedIn" value={formData.linkedIn} onChange={handleChange} className="form-control custom-focus" placeholder="Enter your LinkedIn profile URL" />
        </div>
        <div className="col-md-6">
          <label className="form-label">Company Logo  <span style={{ color: "red" }}>*</span></label>
          {formData.logo && !uploadedLogo && (
            <div className="mb-2">
              <img src={formData.logo} alt="Company Logo" style={{ maxHeight: 80 }} />
            </div>
          )}
          <input type="file" accept="image/*" className="form-control" onChange={handleLogoChange} />
        </div>
        <div className="col-md-6">
          <label htmlFor="aboutUs" className="form-label">About Us <span style={{ color: "red" }}>*</span></label>
          <textarea id="aboutUs" name="aboutus" rows={3} value={formData.aboutUs} onChange={handleChange} className="form-control custom-focus" placeholder="Enter About your company" />
        </div>
      </div>


      <h6>Management Contact</h6>
      <div className="row g-3 mb-4">
        <div className="col-md-6"><label className="form-label">CEO/Proprietor <span style={{ color: "red" }}>*</span></label>
          <input id="ceoName" value={formData.ceoName || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6">
          <label htmlFor="ceoCountryCode" className="form-label">Country Code<span style={{ color: "red" }}>*</span></label>
          <input id="ceoCountryCode" value={formData.ceoCountryCode} onChange={handleChange} className="form-control custom-focus" placeholder="Enter Country Code" />
        </div>
        <div className="col-md-6"><label className="form-label">Mobile <span style={{ color: "red" }}>*</span></label>
          <input id="ceoMobile" value={formData.ceoMobile || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Email <span style={{ color: "red" }}>*</span></label>
          <input id="ceoEmail" value={formData.ceoEmail || ''} onChange={handleChange} className="form-control" /></div>
      </div>


      <h6>Legal Information</h6>
      <div className="row g-3 mb-4">
        <div className="col-md-6"><label className="form-label">License Number</label>
          <input id="businessLicenseNumber" value={formData.businessLicenseNumber || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Company Type</label>
          <select id="type" value={formData.type || ''} onChange={handleChange} className="form-select">
            <option value="">Select</option>
            <option>Sole Proprietorship</option><option>Partnership</option><option>Private Limited</option>
            <option>Public Limited</option><option>LLC</option><option>Corporation</option><option>Others</option>
          </select>
          {formData.type === 'Others' && (
            <input id="specifyType" value={formData.specifyType || ''} onChange={handleChange} className="form-control mt-2" placeholder="Specify Type" />
          )}
        </div>
        <div className="col-md-6"><label className="form-label">Date of Registration</label>
          <input type="date" id="dateOfRegistration" value={formData.dateOfRegistration || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">EIN</label>
          <input id="einNumber" value={formData.einNumber || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">State Tax ID</label>
          <input id="stateTaxId" value={formData.stateTaxId || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">D-U-N-S</label>
          <input id="dunsNumber" value={formData.dunsNumber || ''} onChange={handleChange} className="form-control" /></div>
      </div>


      <h6>Business Profile</h6>
      <div className="row g-3 mb-4">
        <div className="col-md-12"><label className="form-label">Major Products</label>
          <input id="majorProducts" value={formData.majorProducts || ''} onChange={handleChange} className="form-control" /></div>

        {renderArraySection(
          'oemDetails',
          [{ key: 'oemName' }, { key: 'sinceYear' }, { key: 'approxValueUSD' }, { key: 'vendorRating' }],
          ['OEM Name', 'Since Year', 'Approx Value', 'Rating'],
          'Add OEM',
          { oemName: '', sinceYear: '', approxValueUSD: '', vendorRating: '' }
        )}

        {renderArraySection(
          'majorCustomers',
          [{ key: 'name' }, { key: 'address' }, { key: 'productSupplied' }],
          ['Name', 'Address', 'Product Supplied'],
          'Add Customer',
          { name: '', address: '', productSupplied: '' }
        )}

        <div className="col-md-12 mt-3"><label className="form-label">Logistics Provider</label>
          <input id="logisticsProvider" value={formData.logisticsProvider || ''} onChange={handleChange} className="form-control" /></div>

        {renderArraySection(
          'exportDetails',
          [{ key: 'year' }, { key: 'products' }, { key: 'country' }, { key: 'valueUSD' }],
          ['Year', 'Products', 'Country', 'Value USD'],
          'Add Export',
          { year: '', products: '', country: '', valueUSD: '' }
        )}
      </div>


      <h6>Financial Details</h6>
      <div className="row g-3 mb-4">
        {renderArraySection(
          'annualTurnover3yr',
          [{ key: 'year' }, { key: 'turnoverUSD' }],
          ['Year', 'Turnover USD'],
          'Add Year',
          { year: '', turnoverUSD: '' }
        )}

        <div className="col-md-6"><label className="form-label">Bank Name</label>
          <input id="bankName" value={formData.bankName || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Account Number</label>
          <input id="accountNumber" value={formData.accountNumber || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Routing Number</label>
          <input id="routingNumber" value={formData.routingNumber || ''} onChange={handleChange} className="form-control" /></div>
        <div className="col-md-6"><label className="form-label">Account Type</label>
          <input id="accountType" value={formData.accountType || ''} onChange={handleChange} className="form-control" /></div>
      </div>


      <h6>Technical Capabilities</h6>
      <div className="row g-3 mb-4">
        {renderArraySection(
          'factoryLocations',
          [{ key: 'location' }, { key: 'areaSqFt' }, { key: 'registeredAddress' }],
          ['Location', 'Area (sq ft)', 'Registered Address'],
          'Add Factory',
          { location: '', areaSqFt: '', registeredAddress: '' }
        )}

        <div className="col-md-12 mt-3">
          <label className="form-label">Manpower</label>
          <div className="d-none d-md-flex fw-bold gap-2 mb-1">
            {Object.keys(formData.manpower || {}).map((key) => (
              <div key={key} className="flex-fill text-capitalize">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </div>
            ))}
          </div>
          <div className="row g-2">
            {Object.keys(formData.manpower || {}).map((key) => (
              <div className="col-6 col-md" key={key}>
                <div className="d-md-none fw-bold mb-1 text-capitalize">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
                <input
                  id={key}
                  value={formData.manpower?.[key] ?? ''}
                  onChange={handleChange}
                  className="form-control"
                  placeholder={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                />
              </div>
            ))}
          </div>
        </div>

        {renderArraySection(
          'machiningFacilities',
          [{ key: 'facility' }, { key: 'quantity' }, { key: 'remarks' }],
          ['Facility', 'Quantity', 'Remarks'],
          'Add Machining Facility',
          { facility: '', quantity: '', remarks: '' }
        )}

        {renderArraySection(
          'inspectionFacilities',
          [{ key: 'facility' }, { key: 'remarks' }],
          ['Facility', 'Remarks'],
          'Add Inspection Facility',
          { facility: '', remarks: '' }
        )}
      </div>


      <h6>References & Consent</h6>
      <div className="row g-3 mb-4">
        <div className="col-md-12"><label>Project Case Studies</label>
          <textarea id="projectCaseStudies" value={formData.projectCaseStudies || ''} onChange={handleChange} className="form-control" rows={3}></textarea></div>
        <div className="col-md-12"><label>Customer Testimonials</label>
          <textarea id="customerTestimonials" value={formData.customerTestimonials || ''} onChange={handleChange} className="form-control" rows={3}></textarea></div>
        <div className="col-md-12 mt-2">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="confirmCheck" checked={formData.confirmCheck || false} onChange={handleChange} />
            <label className="form-check-label">I confirm that all details are correct</label>
          </div>
        </div>
      </div>


      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-outline-dark" onClick={() => navigate(-1)}>Cancel</button>
        <button className="btn btn-dark" onClick={onSubmit} disabled={!isFormChanged() || isSubmitting}>
          {isSubmitting ? 'Updating...' : 'Update'}
        </button>
      </div>

      <ToastContainer />
      <style>{`
        .chip {
          padding: 6px 12px;
          background: #f3f3f3;
          border-radius: 20px;
        }
        .chip-close {
          margin-left: 8px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default UpdateSellerAdditionalInfoForm;
